import { Routes, Route, Navigate } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import { useAuthStore } from './store/authStore';

// Layout
import Layout from './components/Layout';

// Pages
import HomePage from './pages/HomePage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import SleeperProfilePage from './pages/SleeperProfilePage';
import SleeperProfilePagePreview from './pages/SleeperProfilePagePreview';
import LeaguePage from './pages/LeaguePage';
import DashboardPage from './pages/DashboardPage';
import TopRatedPage from './pages/TopRatedPage';
import LeaguematesPage from './pages/LeaguematesPage';
import AdminPage from './pages/AdminPage';
import LineupHelpPage from './pages/LineupHelpPage';

// Protected route wrapper
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuthStore();

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

// Admin route wrapper
function AdminRoute({ children }: { children: React.ReactNode }) {
  const { user, isAuthenticated, isLoading } = useAuthStore();

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (!user?.isAdmin) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}

function App() {
  // Auth state is initialized by the onAuthStateChanged observer in
  // `store/authStore.ts`, so we just read `isLoading` to know when that
  // initial check is done.
  const { isLoading } = useAuthStore();

  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        bgcolor="background.default"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Routes>
      <Route path="/" element={<Layout />}>
        {/* Public routes */}
        <Route index element={<HomePage />} />
        <Route path="login" element={<LoginPage />} />
        <Route path="register" element={<RegisterPage />} />
        <Route path="top-rated" element={<TopRatedPage />} />
        <Route path="sleeper/:sleeperUserId" element={<SleeperProfilePage />} />
        <Route path="league/:leagueId" element={<LeaguePage />} />
        {/* Design preview — populated profile with hypothetical data
            so we can iterate on the layout. Not linked from the nav;
            navigate to /preview/profile directly. */}
        <Route path="preview/profile" element={<SleeperProfilePagePreview />} />

        {/* Protected routes */}
        <Route
          path="dashboard"
          element={
            <ProtectedRoute>
              <DashboardPage />
            </ProtectedRoute>
          }
        />
        {/* `/profile` was its own page; it's been folded into the
            dashboard. Redirect any lingering bookmarks rather than
            404'ing them. */}
        <Route path="profile" element={<Navigate to="/dashboard" replace />} />
        {/* Lineup Help — live "who's playing today" view across every
            league. Only renders for the verified owner of the Sleeper
            account (the page itself enforces that); ProtectedRoute just
            bounces logged-out visitors to /login. */}
        <Route
          path="sleeper/:sleeperUserId/lineups"
          element={
            <ProtectedRoute>
              <LineupHelpPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="leaguemates"
          element={
            <ProtectedRoute>
              <LeaguematesPage />
            </ProtectedRoute>
          }
        />

        {/* Admin routes */}
        <Route
          path="admin"
          element={
            <AdminRoute>
              <AdminPage />
            </AdminRoute>
          }
        />
      </Route>
    </Routes>
  );
}

export default App;

